/** @jsxImportSource @emotion/react */
import { AxiosError } from "axios";
import {
  Button,
  Checkbox,
  Container,
  DateInput,
  Form,
  Input,
  Message,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import {
  validateAuMobileNumber,
  validateNzMobileNumber,
  validateOver18YearsOld,
  validatePostCode,
} from "library/formValidation";
import { stateOptions, yesNoOptions } from "library/sharedOptions";
import { validateEmail } from "library/validation/customInputValidation";
import {
  createThirdPartyAuthorityTicket,
  GetThirdPartyAuthorityParams,
} from "library/zendeskAPI";
import { Ticket } from "library/zendeskAPI/types";
import { storeRequest } from "library/zendeskAPI/zendeskUtils";
import { useEventTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { Accounts } from "./components/Accounts";
import {
  actionOptions,
  authorityLevelOptions,
  primaryContactOptions,
  removalTypeOptions,
} from "./options";
import { showFormFields } from "./showFormFields";
import { ActionType, ScreenProps, State } from "./types";

export const ThirdPartyAuthorityForm = ({ state, dispatch }: ScreenProps) => {
  const { trackEvent } = useEventTracking();
  const { accountInfo } = state;
  const token = accountInfo.cardToken;

  const thirdPartyAuthorityMutation = useMutation<
    Ticket["id"],
    AxiosError,
    GetThirdPartyAuthorityParams
  >(createThirdPartyAuthorityTicket, {
    onSuccess: (id) => {
      trackEvent({
        event: {
          category: "application",
          action: "application-submitted",
          label: "Submit",
          location: "Third Party Authority",
        },
      });

      storeRequest({
        userEmail: accountInfo.userEmail,
        accountNumber: accountInfo.accountNumber as string,
        requestType: "thirdPartyAuthority",
        ticketId: String(id),
      });

      dispatch({ type: ActionType.FORM_SUBMITTED });
    },
  });

  const onSubmit = ({ errors, values }) => {
    if (Object.keys(errors).length === 0) {
      thirdPartyAuthorityMutation.mutate({
        ...values,
        token,
        userName: accountInfo.userName,
        userEmail: accountInfo.userEmail,
        last4Digits: accountInfo.accountNumber?.slice(-4),
      });
    }
  };

  return (
    <Form initialValues={state.formValues} onSubmit={onSubmit}>
      {({ state: { values: untyped }, setValues }) => {
        const values: State["formValues"] = untyped;
        const accountIds = Object.keys(values.accounts);
        const show = showFormFields({ values });

        return (
          <Stack gap="10">
            <Container padding="4" bg="grey.t05">
              <CreditCard
                cardName={accountInfo.cardName}
                cardEndsWith={accountInfo.cardEndsWith}
              />
            </Container>
            <Stack gap="8" width="100%" width-sm="400">
              <Stack gap="3">
                <Text>
                  <strong>Email address</strong>
                </Text>
                <Text>{accountInfo.userEmail}</Text>
                <Text textStyle="body2">
                  Your letter will be emailed to this address.
                </Text>
              </Stack>
              <Input name="firstName" label="First name" />
              <Input name="lastName" label="Last name" />
              <DateInput
                name="dateOfBirth"
                label="Date of birth"
                validate={validateOver18YearsOld}
              />
              <Input
                type="tel"
                name="mobileNumber"
                label="Mobile number"
                validate={
                  accountInfo.isNZ
                    ? validateNzMobileNumber
                    : validateAuMobileNumber
                }
              />

              <Stack gap="1">
                <Text color="grey.t75">
                  <strong>Product(s) in this request?</strong>
                </Text>

                <Checkbox
                  name="products1"
                  label="Product(s) in this request?"
                  hideLabel
                  optional
                >
                  Credit Card or Store Card
                </Checkbox>

                <Checkbox
                  name="products2"
                  label="Product(s) in this request?"
                  hideLabel
                  optional
                >
                  Personal loan
                </Checkbox>

                <Checkbox
                  name="products3"
                  label="Product(s) in this request?"
                  hideLabel
                  optional
                >
                  Car/Motor Loan
                </Checkbox>
              </Stack>

              <RadioGroup
                name="action"
                label="Would you like to add or remove an authorised party?"
                options={actionOptions}
              />

              {show.removalType && (
                <Select
                  name="removalType"
                  label="Authorised third party type you want removed?"
                  options={removalTypeOptions}
                />
              )}

              {show.addingPoaGuardianOrAdministrator && (
                <Checkbox
                  name="addingPoaGuardianOrAdministrator"
                  label="Adding a Power of Attorney, a Legally Appointed Guardian or Administrator as an Authorised Third Party?"
                  hideLabel
                  optional
                >
                  I am adding a Power of Attorney, a Legally Appointed Guardian
                  or Administrator as an Authorised Third Party
                </Checkbox>
              )}

              {show.authorityLevel && (
                <Select
                  name="authorityLevel"
                  label="What level of authority would you like to apply?"
                  options={authorityLevelOptions}
                />
              )}

              {show.primaryContact && (
                <Select
                  name="primaryContact"
                  label="Primary Contact"
                  options={primaryContactOptions}
                />
              )}

              {show.organisationOrFinancialCounsellor && (
                <RadioGroup
                  name="organisationOrFinancialCounsellor"
                  label="Is your nominated Third Party an Organisation or Financial Counsellor?"
                  options={yesNoOptions}
                />
              )}

              {values.organisationOrFinancialCounsellor === "no" && (
                <Message severity="info-or-minor">
                  You will remain the first point of contact.
                </Message>
              )}

              {show.organisationOrFinancialCounsellorsName && (
                <Input
                  name="organisationOrFinancialCounsellorsName"
                  label="Organisation or Financial Counsellors name"
                />
              )}

              {show.contactName && (
                <Input name="contactName" label="Contact name" optional />
              )}

              {show.nominatedFullName && (
                <Input
                  name="nominatedFullName"
                  label="Full name"
                  helpText="Full name of nominated third party"
                />
              )}
              {show.contactNumber && (
                <Input
                  type="tel"
                  name="contactNumber"
                  label="Contact number"
                  helpText="Contact number of nominated third party"
                />
              )}
              {show.email && (
                <Input
                  name="email"
                  label="Email address"
                  validate={validateEmail}
                />
              )}

              {show.dateOfBirth2 && (
                <DateInput
                  name="dateOfBirth2"
                  label="Date of birth"
                  helpText="Date of birth of nominated third party"
                  validate={validateOver18YearsOld}
                  testId="dateOfBirth2"
                />
              )}

              {show.address && <Input name="address" label="Address" />}
              {show.suburb && <Input name="suburb" label="Suburb or City" />}
              {show.state && (
                <Select name="state" label="State" options={stateOptions} />
              )}

              {show.postcode && (
                <Input
                  name="postcode"
                  label="Postcode"
                  validate={validatePostCode}
                  maxLength="4"
                />
              )}

              <Text>
                Please provide the account number(s) for all accounts related to
                this request
              </Text>

              <Accounts accountIds={accountIds} setValues={setValues} />

              <Textarea
                name="other"
                label="Other relevant information"
                height="100"
              />
            </Stack>

            {thirdPartyAuthorityMutation.isError && (
              <Message severity="blocking" bg="secondary.pink.t30">
                Sorry, there was an error. Please try again later.
              </Message>
            )}
            <Button
              type="submit"
              width="256"
              loading={thirdPartyAuthorityMutation.isLoading}
            >
              Submit
            </Button>
          </Stack>
        );
      }}
    </Form>
  );
};
